import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Menu,
  ChevronDown,
  User,
  Settings,
  LogOut,
  Loader2,
} from "lucide-react";
import { RoutesLink } from "../../utility/RouteLinks";
import { deleteCookies, showToastNotification } from "@/utility/helper";
import { AuthService } from "@/services/Auth/Auth";
import { logout } from "@/store/feature/UserSlice";
import type { RootState } from "@/store/store";
import CommonModal from "@/components/common/CommonModal";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import NotificationDropdown from "@/components/common/NotificationDropdown";
import { ThrowError } from "@/api/callApi";

interface HeaderProps {
  isCollapsed: boolean;
  toggleSidebar: () => void;
  toggleMobileSidebar: () => void;
}

const Header: React.FC<HeaderProps> = ({
  isCollapsed,
  toggleSidebar,
  toggleMobileSidebar,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const { user: userDetails } = useSelector((state: RootState) => state.user);

  const currentDate = new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date());

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      const deviceId = localStorage.getItem(
        import.meta.env.VITE_BUSINESS + "deviceId",
      );
      const response = await AuthService.logout(deviceId || "");
      if (response?.status) {
        showToastNotification("success", response?.message);
        deleteCookies(import.meta.env.VITE_BUSINESS + "token");
        deleteCookies(import.meta.env.VITE_BUSINESS + "refreshToken");
        localStorage.removeItem(import.meta.env.VITE_BUSINESS + "token");
        localStorage.removeItem(import.meta.env.VITE_BUSINESS + "deviceId");

        // Navigate first, then clear the Redux user.
        navigate(RoutesLink?.authRoutes?.login);
        dispatch(logout());
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
      setIsLogoutModalOpen(false);
    }
  };

  return (
    <header className="z-20 bg-white/80 backdrop-blur-xl border-b border-gray-100 shadow-[0_2px_20px_rgba(0,0,0,0.02)] transition-all duration-300">
      <div className="h-14 sm:h-19 px-3 sm:px-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          <div className="">
            <button
              onClick={() => {
                if (window.innerWidth >= 1024) {
                  toggleSidebar();
                } else {
                  toggleMobileSidebar();
                }
              }}
              className="p-2.5 -ml-2 rounded-xl hover:bg-gray-50 text-gray-600 transition-all duration-200 active:scale-95 border border-transparent hover:border-gray-200 cursor-pointer"
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <Menu
                size={22}
                className={`transition-transform duration-300 ${isCollapsed ? "rotate-180" : ""}`}
              />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2  sm:gap-6">
          <div className="hidden md:flex flex-col items-end justify-center">
            <span className="text-[10px] uppercase tracking-widest font-bold text-gray-400 mb-0.5">
              Today
            </span>
            <div className="text-xs text-gray-700 font-medium tracking-wide">
              {currentDate}
            </div>
          </div>

          <div className="w-px h-8 bg-gray-200 hidden md:block mx-1"></div>

          {/* Notification Dropdown */}
          <NotificationDropdown />

          {/* User Profile */}
          <DropdownMenu onOpenChange={(open) => setIsProfileDropdownOpen(open)}>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2.5 p-1.5 pr-3 rounded-full hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-all duration-200 cursor-pointer select-none group focus:outline-none">
                <div className="sm:h-10 sm:w-10 h-8 w-8 rounded-full bg-linear-to-tr from-primary to-primary/80 text-white flex items-center justify-center font-bold text-sm shadow-sm transition-transform duration-200 group-hover:scale-105">
                  {userDetails?.name?.charAt(0).toUpperCase()}
                </div>
                <div className="hidden sm:flex flex-col text-left">
                  <span className="text-xs font-semibold text-gray-700 group-hover:text-gray-900 transition-colors leading-tight">
                    {userDetails?.name}
                  </span>
                  <span className="text-[10px] text-gray-400 font-medium leading-none mt-0.5">
                    {userDetails?.designation?.name || "Employee"}
                  </span>
                </div>
                <ChevronDown
                  size={14}
                  className="text-gray-400 group-hover:text-gray-600 transition-transform duration-200"
                  style={{
                    transform: isProfileDropdownOpen
                      ? "rotate(180deg)"
                      : "none",
                  }}
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 p-1 bg-white">
              <DropdownMenuItem
                onClick={() => navigate(RoutesLink.profile)}
                className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 cursor-pointer rounded-lg"
              >
                <User size={16} className="text-gray-400" />
                <span>Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 cursor-pointer rounded-lg">
                <Settings size={16} className="text-gray-400" />
                <span onClick={() => navigate(RoutesLink.documentSetting)}>
                  Settings
                </span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem
                onClick={() => setIsLogoutModalOpen(true)}
                className="flex items-center gap-3 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 cursor-pointer rounded-lg focus:bg-red-50/50"
              >
                <LogOut size={16} color="#fb2c36" />
                <span className="text-red-500!">Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      <CommonModal
        show={isLogoutModalOpen}
        onClose={() => !isLoading && setIsLogoutModalOpen(false)}
        title=""
        className="max-w-sm rounded-2xl p-0 overflow-hidden"
      >
        <div className="p-6">
          <div className="flex flex-col items-center text-center">
            <div className="h-12 w-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-4">
              <LogOut size={22} className="text-red-500" />
            </div>

            <h3 className="text-lg font-bold text-gray-900 mb-1.5 font-heading">
              Confirm Logout
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed mb-6">
              Are you sure you want to log out? You will need to log in again to
              access your account.
            </p>

            <div className="flex items-center gap-3 w-full">
              <Button
                variant="outline"
                onClick={() => setIsLogoutModalOpen(false)}
                disabled={isLoading}
                className="flex-1 rounded-xl h-11"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleLogout}
                disabled={isLoading}
                className="flex-1 rounded-xl h-11 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin text-red-600" />
                  </>
                ) : (
                  <span>Yes, Logout</span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </CommonModal>
    </header>
  );
};

export default Header;
